import { useQueryClient } from '@tanstack/react-query';

import { useDeleteDraftPlotGroup, usePatchDraftPlotGroup, usePostDraftPlotGroup } from '@/api/rest/resources/plot';
import {
  DraftPlotGroup,
  PatchDraftPlotGroupRequestBodyData,
  PostDraftPlotGroupRequestBodyData,
} from '@/api/rest/resources/types/plot';

type CreateGroupInput = PostDraftPlotGroupRequestBodyData & {
  draft_plot_ids?: string[];
};

export const useCreateDraftPlotGroup = () => {
  const queryClient = useQueryClient();
  const mutation = usePostDraftPlotGroup();

  const submit = async (bodyData: CreateGroupInput): Promise<DraftPlotGroup> => {
    const group = await mutation.mutateAsync({ bodyData });
    queryClient.invalidateQueries({ queryKey: ['land_steward', 'draftplotgroup'] });
    queryClient.invalidateQueries({ queryKey: ['land_steward', 'draftplot'] });
    return group;
  };

  return { submit, isSubmitting: mutation.isPending };
};

export const useUpdateDraftPlotGroup = () => {
  const queryClient = useQueryClient();
  const mutation = usePatchDraftPlotGroup();

  const submit = async (id: string, bodyData: PatchDraftPlotGroupRequestBodyData): Promise<DraftPlotGroup> => {
    const group = await mutation.mutateAsync({ pathVariables: { id }, bodyData });
    // invalidateQueries (not resetQueries) keeps showing the last-known group list while it
    // refetches in the background - resetQueries clears data to undefined synchronously, which
    // crashes any caller (e.g. GroupEditPanel) that stays mounted and re-derives its group from it.
    queryClient.invalidateQueries({ queryKey: ['land_steward', 'draftplotgroup'] });
    return group;
  };

  return { submit, isSubmitting: mutation.isPending };
};

export const useRemoveDraftPlotGroup = () => {
  const queryClient = useQueryClient();
  const mutation = useDeleteDraftPlotGroup();

  const submit = async (id: string) => {
    await mutation.mutateAsync({ pathVariables: { id } });
    queryClient.resetQueries({ queryKey: ['land_steward', 'draftplotgroup'] });
    queryClient.resetQueries({ queryKey: ['land_steward', 'draftplot'] });
  };

  return { submit, isRemoving: mutation.isPending };
};
